import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import '../styles/sitePages.css';
import './FAQ.css';

const faqData = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is Logic Puzzles CA?',
        a: 'Logic Puzzles CA is a free online collection of beautifully crafted logic puzzles and brain games. We offer over 30 different puzzle types that you can play directly in your browser - no downloads required.'
      },
      {
        q: 'Are the puzzles free to play?',
        a: 'Yes! All of our browser-based puzzles are completely free to play. We also offer mobile apps on iOS and Android, some of which include optional in-app purchases for extra content.'
      },
      {
        q: 'Do I need to create an account?',
        a: 'No account is needed. You can start playing any puzzle immediately. Your progress is saved locally in your browser.'
      },
      {
        q: 'What devices and browsers are supported?',
        a: 'Our puzzles work on any modern browser including Chrome, Firefox, Safari, and Edge - on desktop, tablet, and mobile. For the best experience, we recommend using an up-to-date browser.'
      }
    ]
  },
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'How do I start playing?',
        a: 'Just pick a puzzle from our games page and click "Play." Each puzzle includes a brief tutorial or rule explanation to get you started. No sign-up or setup required.'
      },
      {
        q: 'Are there different difficulty levels?',
        a: 'Yes! Most of our puzzles offer multiple difficulty levels, from beginner-friendly to expert. You can choose your preferred difficulty before starting a new puzzle.'
      },
      {
        q: 'Can I play offline?',
        a: 'Our browser puzzles require an internet connection to load. However, our mobile apps (available on iOS and Android) support offline play once downloaded.'
      },
      {
        q: 'What types of logic puzzles do you offer?',
        a: 'We offer a wide variety including Crowns, Nonograms, Calcudoku, Sudoku, Aquarium, Mine Finder, Tent Logic, Fleet, Futoshiki, Nurikabe, and many more. Check out our full collection on the Logic Games page.'
      }
    ]
  },
  {
    category: 'Game Rules & Tips',
    questions: [
      {
        q: 'Where does the Crowns idea come from?',
        a: 'Crowns grows out of a classic chessboard placement problem: put pieces on a board so that no two share a row or column. You place exactly one crown per row, column, and colored region, with the added constraint that crowns cannot be adjacent - even diagonally. Every puzzle has a unique solution reachable through pure logic.'
      },
      {
        q: 'How do Nonogram clues work?',
        a: 'Each row and column has a set of numbers indicating groups of consecutive filled cells. For example, a clue of "3 1" means there is a group of 3 filled cells, then at least one empty cell, then 1 filled cell. Use these clues to deduce which cells to fill.'
      },
      {
        q: 'What are the rules of Calcudoku?',
        a: 'Calcudoku is played on a grid where you fill each row and column with unique numbers (like Sudoku). The grid is divided into cages with a target number and math operation (+, −, ×, ÷). The numbers in each cage must combine using that operation to equal the target.'
      },
      {
        q: 'How does Aquarium work?',
        a: 'In Aquarium, you fill cells with water. Water obeys gravity - if a cell is filled, all cells below it in the same aquarium region must also be filled. Row and column clues tell you exactly how many cells should be filled in each line.'
      },
      {
        q: 'Do any of the puzzles require guessing?',
        a: 'No. Every puzzle on Logic Puzzles CA has a unique solution that can be reached through logical deduction alone. If you find yourself guessing, there is always a logical step you have not yet discovered.'
      }
    ]
  },
  {
    category: 'Printable Puzzles',
    questions: [
      {
        q: 'Can I print puzzles to solve on paper?',
        a: 'Yes! We offer printable versions of several popular puzzles including Crowns, Sudoku, Nonograms, Calcudoku, Aquarium, Binary Sudoku+, and Tent Logic. Visit our printable puzzles section to download and print.'
      },
      {
        q: 'What format are the printable puzzles in?',
        a: 'Printable puzzles are generated as printer-friendly pages that you can print directly from your browser. They are formatted to fit standard letter-size paper.'
      }
    ]
  },
  {
    category: 'Mobile Apps',
    questions: [
      {
        q: 'Do you have mobile apps?',
        a: 'Yes! We have apps available for both iOS and Android. Our apps include Crowns, Aquarium, Binary Sudoku+, Palette Path, Dot Stream, and Daily Logic Puzzles. Visit our Apps page for download links.'
      },
      {
        q: 'Are the mobile apps free?',
        a: 'Our apps are free to download and play. Some apps offer optional in-app purchases to remove ads or unlock additional puzzle packs.'
      }
    ]
  },
  {
    category: 'Technical & Support',
    questions: [
      {
        q: 'Is my progress saved?',
        a: 'Yes, your progress is saved automatically in your browser\'s local storage. Note that clearing your browser data will reset your progress. In our mobile apps, progress is saved to your device.'
      },
      {
        q: 'How do I report a bug or suggest a feature?',
        a: 'We\'d love to hear from you! Please visit our Contact page or email us at ben@sparkandspiral.ca. You can also join our Discord community to chat with us directly.'
      },
      {
        q: 'Why is a puzzle not loading?',
        a: 'Try refreshing the page or clearing your browser cache. Make sure you are using a modern, up-to-date browser. If the issue persists, please contact us and include which puzzle and browser you are using.'
      }
    ]
  }
];

const sectionId = (category) => `faq-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;

// Build FAQPage structured data from all questions
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqData.flatMap(section =>
    section.questions.map(item => ({
      '@type': 'Question',
      'name': item.q,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.a
      }
    }))
  )
};

function FAQ() {
  return (
    <div>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | Logic Puzzles CA</title>
        <meta name="description" content="Find answers to common questions about Logic Puzzles CA. Learn about our free online logic puzzles, game rules, mobile apps, printable puzzles, and more." />
        <meta name="keywords" content="logic puzzles FAQ, puzzle help, how to play logic puzzles, nonogram rules, crowns puzzle rules, calcudoku rules, free online puzzles" />
        <meta property="og:title" content="FAQ - Frequently Asked Questions | Logic Puzzles CA" />
        <meta property="og:description" content="Find answers to common questions about Logic Puzzles CA - free online logic puzzles, game rules, mobile apps, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ - Frequently Asked Questions | Logic Puzzles CA" />
        <meta name="twitter:description" content="Find answers to common questions about Logic Puzzles CA." />
        <link rel="canonical" href="https://logicpuzzles.ca/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <NavBar />
      <div className="faq-page">
        <header className="sp-hero">
          <div className="container">
            <span className="eyebrow">Help &amp; answers</span>
            <h1>Frequently Asked Questions</h1>
            <p className="sp-hero__lead">
              Have a question? Find answers below, or <Link to="/contact">contact us</Link> if you need more help.
            </p>
          </div>
        </header>
        <div className="container sp-main faq-layout">
          <nav className="faq-toc" aria-label="FAQ topics">
            <ul>
              {faqData.map((section) => (
                <li key={section.category}>
                  <a href={`#${sectionId(section.category)}`}>{section.category}</a>
                </li>
              ))}
            </ul>
          </nav>
          <div className="faq-sections">
            {faqData.map((section, sIdx) => (
              <section key={sIdx} id={sectionId(section.category)} className="faq-section">
                <h2>{section.category}</h2>
                <div className="faq-items">
                  {section.questions.map((item, qIdx) => (
                    <details key={qIdx} className="faq-item">
                      <summary>{item.q}</summary>
                      <p>{item.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
